import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";

function WorkerDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const workerFromState = location.state?.worker || null;
  const workerIdFromQuery = searchParams.get("workerId");

  const [worker, setWorker] = useState(workerFromState);
  const [loading, setLoading] = useState(!workerFromState && !!workerIdFromQuery);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchWorker = async () => {
      if (workerFromState || !workerIdFromQuery) return;

      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("workers")
          .select(`
            *,
            profiles (
              id,
              full_name,
              phone,
              city
            )
          `)
          .eq("id", workerIdFromQuery)
          .maybeSingle();

        if (error) throw error;

        if (!data) {
          setErrorMessage("Worker not found.");
          return;
        }

        setWorker({
          id: data.id,
          name: data.profiles?.full_name || "Worker",
          rating: Number(data.average_rating || 0).toFixed(1),
          price: "Price at checkout",
          service: data.nature_of_work || data.skill || "General Service",
          experience: data.skills || "Not specified",
          location: data.online_city || data.profiles?.city || "N/A",
          availability: data.online_status || "offline",
          languages: data.languages || "N/A",
          workerRecord: data,
        });
      } catch (error) {
        console.error("Fetch worker details error:", error);
        setErrorMessage(error.message || "Failed to load worker details.");
      } finally {
        setLoading(false);
      }
    };

    fetchWorker();
  }, [workerFromState, workerIdFromQuery]);

  if (loading) {
    return (
      <div style={page}>
        <div style={card}>
          <h1>Loading worker...</h1>
        </div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div style={page}>
        <div style={card}>
          <h1>{errorMessage || "No worker selected"}</h1>
          <button onClick={() => navigate("/")} style={solidBtn}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={page}>
      <button onClick={() => navigate(-1)} style={backBtn}>
        Back
      </button>

      <div style={card}>
        <h1 style={{ marginTop: 0 }}>{worker.name}</h1>
        <p style={muted}>{worker.service}</p>

        <div style={detailsGrid}>
          <p><strong>Rating:</strong> {worker.rating} ⭐</p>
          <p><strong>Price:</strong> {worker.price}</p>
          <p><strong>Skills:</strong> {worker.experience}</p>
          <p><strong>Location:</strong> {worker.location}</p>
          <p><strong>Availability:</strong> {worker.availability}</p>
          <p><strong>Languages:</strong> {worker.languages}</p>
        </div>

        <div style={aboutBox}>
          <h3 style={{ marginTop: 0 }}>About</h3>
          <p style={{ marginBottom: 0, color: "#d1d1d1" }}>
            {worker.name} is a verified professional available for {String(
              worker.service
            ).toLowerCase()} with local service support and platform tracking.
          </p>
        </div>

        <button
          onClick={() =>
            navigate(`/booking?workerId=${worker.id}`, {
              state: {
                worker: {
                  id: worker.id,
                  name: worker.name,
                  rating: worker.rating,
                  price: worker.price,
                  service: worker.service,
                  location: worker.location,
                  availability: worker.availability,
                  workerRecord: worker.workerRecord,
                },
              },
            })
          }
          style={solidBtn}
        >
          Book Now
        </button>
      </div>
    </div>
  );
}

const page = {
  padding: "40px",
  fontFamily: "Arial, sans-serif",
  maxWidth: "900px",
  margin: "0 auto",
  background: "#000",
  color: "#fff",
  minHeight: "100vh",
};

const card = {
  background: "#111",
  padding: "30px",
  borderRadius: "18px",
  boxShadow: "0 4px 18px rgba(0,0,0,0.18)",
  border: "1px solid #222",
};

const detailsGrid = {
  marginTop: "24px",
  display: "grid",
  gap: "12px",
};

const aboutBox = {
  marginTop: "24px",
  background: "#0b0b0b",
  padding: "18px",
  borderRadius: "12px",
  border: "1px solid #222",
};

const backBtn = {
  marginBottom: "20px",
  padding: "10px 16px",
  border: "1px solid #444",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const solidBtn = {
  marginTop: "24px",
  padding: "12px 20px",
  border: "none",
  background: "#fff",
  color: "#000",
  borderRadius: "10px",
  cursor: "pointer",
  fontWeight: "bold",
};

const muted = {
  color: "#d1d1d1",
  marginTop: "6px",
};

export default WorkerDetails;
